import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import {
  CONTRACTOR_STATUS_LABELS,
  formatMoney,
  MAX_CONTRACTOR_DISCOUNT_PERCENT,
  percentOffPrice,
  regularPriceOf,
  type AdminContractor,
  type ContractorPricing,
  type ContractorPricingUpdate,
  type ContractorStatus,
  type SearchSuggestion,
} from "@neus/shared";
import { api, ApiError } from "../api/client";
import { useAllContractorPricing, useMe, useProduct } from "../api/hooks";
import { HardHatIcon, TagIcon } from "../components/Icons";
import { SearchBox } from "../components/search/SearchBox";
import { IconCard, LoadError, PageTitle } from "../components/ui/IconCard";
import styles from "./Admin.module.css";
import { features } from "../features";
import { STAFF_PAGES } from "../nav";

const message = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong. Please try again.");

/** "12.50" → 1250 cents; null when it isn't a price. */
const toCents = (dollars: string) => {
  const t = dollars.trim().replace(/^\$/, "");
  return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : null;
};

/**
 * Staff-only pages: the Entra "Admin" role, confirmed by a sign-in within 12 hours (the API says
 * when it needs a fresh one). The API enforces all of it; this only keeps others out of the UI.
 */
export function RequireAdmin({ title, children }: { title: string; children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const { pathname } = useLocation();
  const signIn = `/api/auth/login?next=${encodeURIComponent(pathname)}`;
  if (isLoading) return <p className="muted">Loading…</p>;
  if (!me?.isAdmin) {
    return (
      <>
        <PageTitle>{title}</PageTitle>
        <IconCard icon={<HardHatIcon />} title="For store staff">
          <p>
            These pages are for staff with the Admin role.{" "}
            {me ? "If you were just given it, sign out and back in." : <a href={signIn}>Sign in</a>}
          </p>
        </IconCard>
      </>
    );
  }
  return <>{children}</>;
}

/** An API error that asks for a fresh sign-in (the role is re-checked every 12 hours). */
export function AdminError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { pathname } = useLocation();
  if (error instanceof ApiError && (error.body as { recheck?: boolean } | undefined)?.recheck) {
    return (
      <div className="notice">
        For security, admin tools need a recent sign-in.{" "}
        <a href={`/api/auth/login?next=${encodeURIComponent(pathname)}`}>Sign in again</a>.
      </div>
    );
  }
  return <LoadError onRetry={onRetry}>{message(error)}</LoadError>;
}

/** The staff pages: orders to fulfill, completed orders, and contractor pricing. */
export function AdminTabs() {
  const tab = ({ isActive }: { isActive: boolean }) => (isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab);
  return (
    <nav className={styles.tabs} aria-label="Staff pages">
      {STAFF_PAGES.map(({ label, to, end }) => (
        <NavLink key={to} to={to} end={end} className={tab}>
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function ContractorList() {
  const [q, setQ] = useState("");
  const allPricing = useAllContractorPricing();
  // One request for the list, filtered here as staff type (the server scans the users table).
  const {
    data: all,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-contractors"],
    queryFn: () => api.adminContractors(),
    retry: false,
  });
  const term = q.trim().toLowerCase();
  const data = all?.filter(
    (c) => !term || [c.name, c.email, c.companyName].some((v) => v?.toLowerCase().includes(term)),
  );
  return (
    <div className={styles.stack}>
      <PageTitle
        subtitle={
          !features.contractorPricing
            ? "Review and activate contractor accounts."
            : features.contractorApproval
              ? "Activate contractor accounts, and set job order discounts and special prices for select contractors."
              : "Set job order discounts and special prices for select contractors."
        }
      >
        Contractors
      </PageTitle>
      <AdminTabs />
      {features.allContractorPricing && !allPricing.isLoading && !allPricing.error && (
        <AllContractorsNote pricing={allPricing.data} edit={false} />
      )}
      <IconCard icon={<HardHatIcon />} title="Contractor accounts">
        <label className={styles.field}>
          <span>Search by name, email or company</span>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} type="search" />
        </label>
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : error ? (
          <AdminError error={error} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          <p className="muted">{term ? "No contractors match." : "No contractor accounts yet."}</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Contractor</th>
                <th scope="col">Company</th>
                {features.contractorApproval && <th scope="col">Status</th>}
                {features.contractorPricing && <th scope="col">Own pricing</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/admin/contractors/${encodeURIComponent(c.id)}`}>{c.name || c.email || c.id}</Link>
                    {c.email && c.name && <div className="muted">{c.email}</div>}
                  </td>
                  <td>{c.companyName ?? "—"}</td>
                  {features.contractorApproval && <td>{c.status && <StatusChip status={c.status} />}</td>}
                  {features.contractorPricing && (
                    <td>
                      {c.discountPercent || c.specialPrices ? (
                        <span className={styles.pricingChip}>
                          {[
                            c.discountPercent ? `${c.discountPercent}% off job orders` : "",
                            c.specialPrices
                              ? `${c.specialPrices} special price${c.specialPrices === 1 ? "" : "s"}`
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      ) : (
                        <span className="muted">None of their own</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </IconCard>
    </div>
  );
}

export function AdminContractorsPage() {
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireAdmin title="Contractors">
          <ContractorList />
        </RequireAdmin>
      </div>
    </div>
  );
}

interface Row {
  sku: string;
  /** Dollars as typed. */
  price: string;
}

/** One special price row: the product (fetched for its name and regular price), and the savings. */
function PriceRow({
  row,
  allPrice,
  onChange,
  onRemove,
}: {
  row: Row;
  /** The all-contractors special price for this product, on a contractor's own pricing. */
  allPrice?: number;
  onChange: (price: string) => void;
  onRemove: () => void;
}) {
  const { data: product, isError } = useProduct(row.sku);
  const cents = toCents(row.price);
  // Special prices are set against the regular price, not a sale that will end.
  const regular = product ? regularPriceOf(product) : undefined;
  const onSale = product !== undefined && regular !== product.price;
  // Savings to one decimal (7.5% off); "lower" is decided on exact cents.
  const savings = regular !== undefined && cents !== null ? Math.round((1 - cents / regular) * 1000) / 10 : null;
  const lower = regular !== undefined && cents !== null && cents < regular;
  // The "% off" calculator: type a percent, and it sets the special price from the regular one.
  const [percent, setPercent] = useState<string | null>(null);
  const [percentError, setPercentError] = useState(false);
  const percentButton = useRef<HTMLButtonElement>(null);
  const name = product?.name ?? row.sku;
  const closeCalculator = () => {
    setPercent(null);
    setPercentError(false);
    // Back to the button that opened it, so keyboard focus doesn't fall to the page.
    requestAnimationFrame(() => percentButton.current?.focus());
  };
  const applyPercent = () => {
    const price = regular !== undefined && percent !== null ? percentOffPrice(regular, percent) : null;
    if (price === null) return setPercentError(true);
    onChange((price / 100).toFixed(2));
    closeCalculator();
  };
  return (
    <tr>
      <td>
        {product ? <Link to={`/products/${encodeURIComponent(row.sku)}`}>{product.name}</Link> : <span>{row.sku}</span>}
        <div className="muted">{row.sku}</div>
        {isError && <div className={styles.fieldError}>Not in the catalog anymore: remove it to save.</div>}
      </td>
      <td className={styles.num}>
        {regular !== undefined ? formatMoney(regular) : "—"}
        {onSale && <div className={`muted ${styles.saleNote}`}>On sale: {formatMoney(product.price)}</div>}
      </td>
      <td className={styles.num}>
        <div className={styles.priceCell}>
          <input
            className={`input ${styles.money}`}
            value={row.price}
            onChange={(e) => onChange(e.target.value)}
            inputMode="decimal"
            aria-label={`Special price for ${name}`}
            aria-invalid={cents === null || undefined}
          />
          <button
            ref={percentButton}
            type="button"
            className={`btn btn--ghost ${styles.percentButton}`}
            aria-expanded={percent !== null}
            aria-label={`Set the special price for ${name} as a percent off`}
            disabled={!product}
            onClick={() => {
              setPercent((p) => (p === null ? "" : null));
              setPercentError(false);
            }}
          >
            % off
          </button>
        </div>
        {percent !== null && (
          <div className={styles.percentCalc}>
            <input
              className={`input ${styles.percentInput}`}
              value={percent}
              onChange={(e) => {
                setPercent(e.target.value);
                setPercentError(false);
              }}
              onKeyDown={(e) => {
                // Enter applies the percent here, rather than saving the whole form.
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyPercent();
                }
                if (e.key === "Escape") closeCalculator();
              }}
              inputMode="decimal"
              placeholder="15"
              autoFocus
              aria-label={`Percent off the regular price of ${name}`}
              aria-invalid={percentError || undefined}
              aria-describedby={percentError ? `percent-error-${row.sku}` : undefined}
            />
            <span aria-hidden="true">%</span>
            <button type="button" className="btn btn--ghost" onClick={applyPercent}>
              Apply
            </button>
            {percentError && (
              <span id={`percent-error-${row.sku}`} className={styles.fieldError}>
                Enter a percent like 15 or 12.5, below 100.
              </span>
            )}
          </div>
        )}
      </td>
      <td className={styles.num}>
        {savings === null ? (
          <span className={styles.fieldError}>Enter a price</span>
        ) : !lower ? (
          <span className={styles.fieldError}>Not lower</span>
        ) : (
          <>
            {`${savings}% off`}
            {/* The contractor pays the lower of the two, so the sale wins while it lasts. */}
            {onSale && cents !== null && cents >= product.price && (
              <div className="muted">Sale price applies until the sale ends</div>
            )}
            {/* A heads-up, not an error: their own price always wins, even when it's the worse deal. */}
            {allPrice !== undefined && cents !== null && cents > allPrice && product && allPrice < product.price && (
              <div className="muted">Higher than the all-contractors {formatMoney(allPrice)}; theirs applies</div>
            )}
          </>
        )}
      </td>
      <td>
        <button type="button" className="btn btn--ghost" onClick={onRemove}>
          Remove
        </button>
      </td>
    </tr>
  );
}

/**
 * A job order discount and special prices: one contractor's, or the ones for all contractors.
 * `onSave` stores them and returns what the server kept.
 */
function PricingEditor({
  pricing,
  onSave,
  allPrices,
  savedText,
  back,
}: {
  pricing: ContractorPricing | undefined;
  /** On a contractor's page: the all-contractors special prices, which theirs override. */
  allPrices?: Record<string, number>;
  onSave: (update: ContractorPricingUpdate) => Promise<ContractorPricing | undefined>;
  savedText: string;
  back: { to: string; label: string };
}) {
  const [discount, setDiscount] = useState(String(pricing?.discountPercent ?? 0));
  const [rows, setRows] = useState<Row[]>(() =>
    Object.entries(pricing?.prices ?? {}).map(([sku, cents]) => ({ sku, price: (cents / 100).toFixed(2) })),
  );
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const add = (s: SearchSuggestion) =>
    setRows((rs) =>
      rs.some((r) => r.sku === s.sku) ? rs : [...rs, { sku: s.sku, price: (s.price / 100).toFixed(2) }],
    );

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    const percent = Number(discount);
    if (!Number.isFinite(percent) || percent < 0 || percent > MAX_CONTRACTOR_DISCOUNT_PERCENT) {
      return setStatus({ ok: false, text: `The discount must be 0 to ${MAX_CONTRACTOR_DISCOUNT_PERCENT}%.` });
    }
    const prices: Record<string, number> = {};
    for (const r of rows) {
      const cents = toCents(r.price);
      if (cents === null || cents < 1) return setStatus({ ok: false, text: `Check the special price for ${r.sku}.` });
      prices[r.sku] = cents;
    }
    const update: ContractorPricingUpdate = { discountPercent: percent, prices };
    setSaving(true);
    try {
      const saved = await onSave(update);
      // Show what the server kept (e.g. cleared pricing), without remounting the form.
      setDiscount(String(saved?.discountPercent ?? 0));
      setRows(Object.entries(saved?.prices ?? {}).map(([sku, cents]) => ({ sku, price: (cents / 100).toFixed(2) })));
      setStatus({ ok: true, text: savedText });
    } catch (err) {
      setStatus({ ok: false, text: message(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.stack} onSubmit={save}>
      <IconCard icon={<TagIcon />} title="Job order discount">
        <label className={styles.field}>
          <span>Percent off job orders (lines without a special price)</span>
          <span className={styles.percent}>
            <input
              className={`input ${styles.money}`}
              type="number"
              min={0}
              max={MAX_CONTRACTOR_DISCOUNT_PERCENT}
              step={0.5}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
            %
          </span>
        </label>
        <p className="muted">Applies to job orders only; regular checkout uses special prices alone.</p>
      </IconCard>

      <IconCard icon={<HardHatIcon />} title="Special prices">
        <p className="muted">
          Shown with the contractor price tag while they shop, and charged at checkout and on job orders.
        </p>
        <SearchBox onPick={add} clearOnPick label="Add a product" placeholder="Search to add a product…" />
        {rows.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Product</th>
                <th scope="col" className={styles.num}>
                  Regular
                </th>
                <th scope="col" className={styles.num}>
                  Special price ($)
                </th>
                <th scope="col" className={styles.num}>
                  Savings
                </th>
                <th scope="col">
                  <span className="visually-hidden">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <PriceRow
                  key={r.sku}
                  row={r}
                  allPrice={allPrices?.[r.sku]}
                  onChange={(price) => setRows((rs) => rs.map((x) => (x.sku === r.sku ? { ...x, price } : x)))}
                  onRemove={() => setRows((rs) => rs.filter((x) => x.sku !== r.sku))}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No special prices yet.</p>
        )}
      </IconCard>

      {status && <div className={status.ok ? "notice" : "notice notice--warn"}>{status.text}</div>}
      <div className={styles.row}>
        <button className="btn btn--lg" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save pricing"}
        </button>
        <Link to={back.to}>{back.label}</Link>
        {pricing?.updatedAt && (
          <span className="muted">
            Last changed {new Date(pricing.updatedAt).toLocaleString()} by {pricing.updatedBy}
          </span>
        )}
      </div>
    </form>
  );
}

/** A contractor account's approval status, as a chip. */
function StatusChip({ status }: { status: ContractorStatus }) {
  return <span className={`${styles.status} ${styles[`status_${status}`]}`}>{CONTRACTOR_STATUS_LABELS[status]}</span>;
}

/** With contractor approval on: the account's status, and the button that activates or deactivates it. */
function ContractorStatusCard({ contractor }: { contractor: AdminContractor }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const status = contractor.status ?? "pending";
  const activate = status !== "active";
  const change = async () => {
    setSaving(true);
    setError(undefined);
    try {
      const saved = await api.updateContractorStatus(contractor.id, activate);
      qc.setQueryData(["admin-contractor", contractor.id], saved);
      void qc.invalidateQueries({ queryKey: ["admin-contractors"] });
    } catch (err) {
      setError(message(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <IconCard icon={<HardHatIcon />} title="Account status">
      <p className={styles.row}>
        <StatusChip status={status} />
        {contractor.statusChangedAt && (
          <span className="muted">
            {status === "active" ? "Activated" : "Deactivated"} {new Date(contractor.statusChangedAt).toLocaleString()}{" "}
            by {contractor.statusChangedBy}
          </span>
        )}
      </p>
      <p className="muted">
        {status === "active"
          ? "They can place job orders and get contractor prices. Deactivating stops both; their job orders stay."
          : status === "pending"
            ? "They can't place job orders or get contractor prices until you activate the account."
            : "Deactivated: they can't place job orders or get contractor prices until it's activated again."}
      </p>
      {(contractor.companyPhone || contractor.companyEmail) && (
        <p>{[contractor.companyPhone, contractor.companyEmail].filter(Boolean).join(" · ")}</p>
      )}
      {error && <div className="notice notice--warn">{error}</div>}
      <div className={styles.row}>
        <button
          type="button"
          className={activate ? "btn" : "btn btn--ghost"}
          disabled={saving || !contractor.isContractor}
          onClick={() => void change()}
        >
          {saving ? "Saving…" : activate ? "Activate account" : "Deactivate account"}
        </button>
      </div>
    </IconCard>
  );
}

/** What the all-contractors pricing adds, on a contractor's page and the contractor list. */
function AllContractorsNote({ pricing, edit }: { pricing: ContractorPricing | null | undefined; edit: boolean }) {
  const count = Object.keys(pricing?.prices ?? {}).length;
  const parts = [
    pricing?.discountPercent ? `${pricing.discountPercent}% off job orders` : "",
    count ? `${count} special price${count === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  return (
    <div className="notice">
      {parts.length ? (
        <>
          <strong>All contractors:</strong> {parts.join(" · ")}.{" "}
          {edit
            ? "This contractor's own special prices always win, and their own job order discount replaces it when it's above 0."
            : "Each contractor's own prices win over these."}
        </>
      ) : (
        "No pricing for all contractors yet."
      )}{" "}
      <Link to="/admin/contractors/all">{parts.length ? "Edit" : "Set it up"}</Link>
    </div>
  );
}

function ContractorDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const all = useAllContractorPricing();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-contractor", id],
    queryFn: () => api.adminContractor(id),
    retry: false,
  });
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !data) return <AdminError error={error} onRetry={() => void refetch()} />;
  return (
    <div className={styles.stack}>
      <PageTitle subtitle={[data.companyName, data.email].filter(Boolean).join(" · ") || undefined}>
        {data.name || data.email || "Contractor"}
      </PageTitle>
      {!data.isContractor && (
        <div className="notice notice--warn">
          This account isn&rsquo;t a contractor right now
          {features.contractorPricing ? ", so its pricing doesn’t apply." : "."}
        </div>
      )}
      {features.contractorApproval && <ContractorStatusCard contractor={data} />}
      {features.allContractorPricing && data.isContractor && !all.isLoading && !all.error && (
        <AllContractorsNote pricing={all.data} edit />
      )}
      {features.contractorPricing ? (
        <PricingEditor
          key={data.id}
          pricing={data.contractorPricing}
          allPrices={data.isContractor ? all.data?.prices : undefined}
          onSave={async (update) => {
            const saved = await api.updateContractorPricing(data.id, update);
            qc.setQueryData(["admin-contractor", data.id], saved);
            void qc.invalidateQueries({ queryKey: ["admin-contractors"] });
            return saved.contractorPricing;
          }}
          savedText="Saved. The contractor sees the new prices on their next page load."
          back={{ to: "/admin/contractors", label: "All contractors" }}
        />
      ) : (
        <Link to="/admin/contractors">All contractors</Link>
      )}
    </div>
  );
}

function AllContractorPricing() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useAllContractorPricing();
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error) return <AdminError error={error} onRetry={() => void refetch()} />;
  return (
    <div className={styles.stack}>
      <PageTitle subtitle="Applies to every contractor account. A contractor's own special prices always win over these, and their own job order discount replaces this one when it's above 0.">
        Pricing for all contractors
      </PageTitle>
      <AdminTabs />
      <PricingEditor
        pricing={data ?? undefined}
        onSave={async (update) => {
          const saved = await api.updateAllContractorPricing(update);
          qc.setQueryData(["admin-all-pricing"], saved);
          return saved ?? undefined;
        }}
        savedText="Saved. Contractors see the new prices on their next page load (within a minute)."
        back={{ to: "/admin/contractors", label: "Contractor accounts" }}
      />
    </div>
  );
}

export function AdminAllContractorPricingPage() {
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireAdmin title="Pricing for all contractors">
          <AllContractorPricing />
        </RequireAdmin>
      </div>
    </div>
  );
}

export function AdminContractorPage() {
  const { id = "" } = useParams();
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireAdmin title="Contractor pricing">
          <ContractorDetail id={id} />
        </RequireAdmin>
      </div>
    </div>
  );
}
